import { produce } from "immer";
import { createContext, useCallback, useContext } from "react";

import {
	objectToTuples,
	removeTrailingSlashes,
	trimObj,
	updateURLSearchParams,
} from "@/utils";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type {
	GETArgs,
	POSTArgs,
	ServiceProviderProps,
	SrpApiContext,
} from "./types";

const queryClient = new QueryClient();
const Context = createContext<SrpApiContext | null>(null);

/**
 * Custom hook to access the SRP API context.
 *
 * @returns {Object} The SRP API context object containing:
 * - getFn: Function for making GET requests
 * - postFn: Function for making POST requests
 * - baseURL: The configured base URL for requests
 *
 * @throws {Error} When used outside of a ServiceProvider
 *
 * @example
 * const { getFn, postFn, baseURL } = useSrpApiContext();
 */
export const useSrpApiContext = () => useContext(Context);

/**
 * A React context provider component that manages API service requests.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.url - The base URL for API requests
 * @param {Object} [props.baseParams] - Optional base parameters to be included in all requests
 * @param {React.ReactNode} props.children - Child components that will have access to the SRP API context
 * @returns {JSX.Element} A Context Provider component wrapping the children
 *
 * @example
 * <ServiceProvider url="https://api.example.com" baseParams={{ audience: 100, fund_id: 101, version: "preview" }}>
 *   <ChildComponent />
 * </ServiceProvider>
 *
 * @description
 * This provider component creates a context that exposes GET and POST functions for making API requests.
 * It handles:
 * - URL parameter management
 * - CSRF token inclusion for POST requests
 * - Response type detection (JSON, text, zip)
 * - Basic error handling
 * - Parameter cleaning and transformation
 *
 * The provided context includes:
 * - getFn: Function for making GET requests
 * - postFn: Function for making POST requests
 * - baseURL: The configured base URL for requests
 */
export default function ServiceProvider({
	url,
	baseParams,
	children,
}: ServiceProviderProps) {
	const baseURL = url;
	const getFn = useCallback(
		async (args: GETArgs) => {
			const cleanedBaseParams = trimObj(baseParams ?? {});
			const cleanedParams = trimObj(args.params ?? {});
			const _params = objectToTuples({
				...cleanedBaseParams,
				...cleanedParams,
			});

			const urlParams = new URLSearchParams(_params);
			const urlObj = new URL(args.baseURL ?? baseURL);
			urlObj.pathname = `${removeTrailingSlashes(urlObj.pathname)}${args.pathname}`;

			if (urlObj.protocol === "http:") {
				urlObj.protocol = "https:";
			}

			const response = await fetch(
				updateURLSearchParams(urlObj, urlParams).toString(),
				{
					method: "GET",
				},
			);

			if (!response.ok) throw new Error(response.statusText);
			const isJson = response.headers
				.get("content-type")
				?.includes("application/json");
			return isJson ? await response.json() : await response.text();
		},
		[baseParams, baseURL],
	);

	const postFn = useCallback(
		async (args: POSTArgs) => {
			const cleanedBaseParams = args.params ? trimObj(baseParams ?? {}) : {};
			const cleanedParams = args.params ? trimObj(args.params ?? {}) : {};
			const params = args.params
				? objectToTuples({ ...cleanedBaseParams, ...cleanedParams })
				: [];

			const urlParams = new URLSearchParams(params);
			const urlParamsSize = [...urlParams.keys()].length;

			const init: RequestInit = produce(
				{ body: args.body, ...args.requestInit } as RequestInit,
				(draft) => {
					if (args.csrftoken) {
						if (Array.isArray(draft.headers)) {
							draft.headers.push(["X-CSRFToken", args.csrftoken]);
						} else {
							draft.headers = {
								...draft.headers,
								"X-CSRFToken": args.csrftoken,
							};
						}
					}
					draft.method = draft.method ?? "POST";
					draft.mode = draft.mode ?? "cors";
					draft.credentials = draft.credentials ?? "same-origin";
				},
			);
			const response = await fetch(
				`${args.baseURL ?? baseURL}${args.pathname}${urlParamsSize > 0 ? `?${urlParams}` : ""}`,
				init,
			);
			if (!response.ok) {
				throw new Error(response.statusText);
			}
			const isZip = response.headers
				.get("content-type")
				?.includes("application/zip");
			return await (isZip ? response.blob() : response.text());
		},
		[baseParams, baseURL],
	);
	return (
		<Context.Provider value={{ getFn, postFn, baseURL }}>
			<QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
		</Context.Provider>
	);
}
